<template>
  <li class="tree-item-container">
    <a
      v-if="node.linkUrl"
      :href="node.linkUrl"
      :class="{ 'is-active': isActive }"
      @click.prevent="emitClick(node)"
    >
      {{ node.label }}
    </a>
    <ul v-show="isExpanded" v-if="node.children.length > 0">
      <UiTree
        v-for="(child, indexNode) in node.children"
        :key="child?.id ? child.id : indexNode"
        :index="indexNode"
        :node="child"
        @expanded="expandUpwards"
        @clicked="emitClick"
      />
    </ul>
  </li>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue'
import type { TreeNode } from './types'

interface Props {
  node: TreeNode
  index?: number
}

const props = withDefaults(defineProps<Props>(), {
  index: -1,
})

const emit = defineEmits(['clicked', 'expanded'])

const isExpanded = ref(!!props.node?.isActive)

const expandUpwards = () => {
  isExpanded.value = true
  emitOnExpand()
}

const emitOnExpand = () => {
  const emitData = {
    index: props.index,
  }
  emit('expanded', emitData)
}

const emitClick = (node: TreeNode): void => {
  // This is done in this manner so that the consuming component can decide which property they need
  const noChildrenNode = { ...node } as {
    label: string
    linkUrl?: string
    isActive: boolean
    [x: string]: any
    children?: TreeNode[]
  }

  delete noChildrenNode.children
  emit('clicked', noChildrenNode)
}

const isActive = computed(() => {
  return props.node?.isActive
})

watch(
  () => props.node,
  () => {
    if (props.node?.isActive) {
      expandUpwards()
    } else isExpanded.value = false
  },
  { immediate: true }
)
</script>

<style lang="scss" scoped>
.tree-item-container {
  text-transform: capitalize;
}

.tree-item-container > a {
  color: var(--color-base-black);
  text-decoration: none;
  @extend .body-m;
}

.is-active {
  color: var(--color-base-red-100) !important;
}

li {
  line-height: 18px;
  list-style: none;
  padding: 2px 0;
}

ul {
  margin: 0;
  padding-left: 20px;
  padding-top: 6px;
}
</style>
